import PropTypes from 'prop-types';
import { Component } from 'react';
import { NavMarker } from 'react-rhumb';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UILink from 'UIComponents/link/UILink';
import UIButton from 'UIComponents/button/UIButton';
import UICopyButton from 'UIComponents/button/UICopyButton';
import UIImage from 'UIComponents/image/UIImage';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIHeader from 'UIComponents/layout/UIHeader';
import UIListingPage from 'UIComponents/page/UIListingPage';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import UIIllustration from 'UIComponents/image/UIIllustration';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import text from 'I18n/utils/unescapedText';
import { fileSize } from 'FileManagerCore/utils/fileSize';
import { FileTypes } from 'FileManagerCore/Constants';
import { getSignedUrlRedirectViewUrl, getSignedUrlRedirectDownloadUrl, fetchSingleFile } from 'FileManagerCore/api/Files';
import LoadingFailed from 'FileManagerCore/components/LoadingFailed';
import { getIsFileAccessibleAnonymously, getIsFileSensitive } from 'FileManagerCore/utils/fileAccessibility';
import { reportError } from 'FileManagerCore/utils/logging';
import getIsViewButtonDisabled from '../helpers/getIsViewButtonDisabled';
import htmlEscape from '../helpers/htmlEscape';
import usageTracker from '../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const getfileInteractionTracker = interactionType => () => usageTracker.track('fileInteraction', {
  interactionType
});
class Page extends Component {
  constructor(props) {
    super(props);
    this.state = {
      extension: null,
      fileName: null,
      size: null,
      type: null,
      viewUrl: null,
      downloadUrl: null,
      errorCode: null,
      isLoading: false,
      isSensitive: true
    };
  }
  componentDidMount() {
    const fileId = Number(this.props.fileId);
    fetchSingleFile(fileId).then(file => {
      const extension = file.get('extension');
      const fileName = `${file.get('name')}.${extension}`;
      document.title = `${text('FileManagerPreview.title')} | ${htmlEscape(fileName)}`;
      this.setState({
        extension,
        fileName,
        size: file.get('size'),
        type: file.get('type'),
        viewUrl: getSignedUrlRedirectViewUrl(fileId),
        downloadUrl: getSignedUrlRedirectDownloadUrl(fileId),
        isLoading: false,
        isSensitive: getIsFileSensitive(file)
      });
      usageTracker.track('pageView', {
        fileType: file.get('type'),
        hidden: file.get('hidden'),
        allowsAnonymousAccess: getIsFileAccessibleAnonymously(file)
      });
    }).catch(err => {
      reportError(err, {
        type: 'FETCH_SINGLE_FILE_FAILED'
      });
      this.setState({
        errorCode: err.status || 'TIMEOUT',
        isLoading: false
      });
    });
    this.setState({
      isLoading: true
    });
  }
  renderClickableImage(viewUrl) {
    const {
      downloadUrl
    } = this.state;
    return /*#__PURE__*/_jsx(UILink, {
      href: downloadUrl || undefined,
      download: true,
      children: /*#__PURE__*/_jsx(UIImage, {
        "data-test-id": "file-preview-image",
        src: viewUrl,
        style: {
          maxWidth: 600
        }
      })
    });
  }
  renderImage() {
    const {
      viewUrl,
      type,
      extension
    } = this.state;
    if (!viewUrl) {
      return null;
    }
    if (type === FileTypes.IMG && !getIsViewButtonDisabled(extension)) {
      return this.renderClickableImage(viewUrl);
    }
    if (type === FileTypes.IMG) {
      return /*#__PURE__*/_jsx(UIImage, {
        "data-test-id": "file-preview-image",
        src: viewUrl,
        style: {
          maxWidth: 600
        }
      });
    } else if (extension === 'pdf') {
      return /*#__PURE__*/_jsx("iframe", {
        "data-test-id": "file-preview-image",
        src: viewUrl,
        style: {
          minWidth: 800,
          height: 800
        }
      });
    }
    return /*#__PURE__*/_jsx(UIIcon, {
      name: "documents",
      size: "xl",
      className: "p-all-6"
    });
  }
  renderSize() {
    const {
      size
    } = this.state;
    if (!size) {
      return null;
    }
    return /*#__PURE__*/_jsx("p", {
      children: fileSize(size)
    });
  }
  renderNotFound() {
    return /*#__PURE__*/_jsxs("div", {
      className: "text-center",
      children: [/*#__PURE__*/_jsx(UIIllustration, {
        className: "m-y-10",
        name: "errors/general",
        width: 100
      }), /*#__PURE__*/_jsx("h4", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "FileManagerPreview.notFound.title"
        })
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "FileManagerPreview.notFound.message"
        })
      }), /*#__PURE__*/_jsx(NavMarker, {
        name: "FILE_PREVIEW_NOT_FOUND"
      })]
    });
  }
  renderFailed() {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(LoadingFailed, {}), /*#__PURE__*/_jsx(NavMarker, {
        name: "FILE_PREVIEW_FAILED"
      })]
    });
  }
  renderBody() {
    const {
      viewUrl,
      fileName,
      errorCode,
      isLoading
    } = this.state;
    if (isLoading) {
      return /*#__PURE__*/_jsx(UILoadingOverlay, {});
    } else if (errorCode) {
      return errorCode === 404 ? this.renderNotFound() : this.renderFailed();
    } else if (!viewUrl) {
      return /*#__PURE__*/_jsx("div", {});
    }
    return /*#__PURE__*/_jsxs("div", {
      className: "flex-column align-center",
      children: [/*#__PURE__*/_jsx("div", {
        className: "align-center justify-center",
        children: this.renderImage()
      }), /*#__PURE__*/_jsx("h3", {
        className: "m-top-6",
        "data-reagan-selector": "filename",
        children: fileName
      }), this.renderSize(), /*#__PURE__*/_jsx(NavMarker, {
        name: "FILE_PREVIEW_LOADED"
      })]
    });
  }
  renderShare24HourLinkButton() {
    const {
      viewUrl,
      extension,
      isSensitive
    } = this.state;
    const isButtonDisabled = getIsViewButtonDisabled(extension);
    if (isSensitive) {
      return null;
    }
    return /*#__PURE__*/_jsx(UITooltip, {
      maxWidth: 300,
      disabled: !isButtonDisabled,
      placement: "right",
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `FileManagerPreview.viewButton.disabled.${extension.toLowerCase()}`
      }),
      children: /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "file-preview-share-24-hour-link-button",
        use: "secondary",
        href: viewUrl,
        onClick: getfileInteractionTracker('view'),
        disabled: isButtonDisabled,
        "data-reagan-selector": "share-24Hour-link-button",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "FileManagerPreview.share24HourLinkButton"
        })
      }, "share-24Hour-link-button")
    }, "file-preview-share-24-hour-link-button-tooltip");
  }
  renderButtons() {
    const {
      downloadUrl
    } = this.state;
    if (!downloadUrl) {
      return null;
    }
    return [/*#__PURE__*/_jsx(UIButton, {
      "data-test-id": "file-preview-download-button",
      use: "primary",
      href: downloadUrl,
      download: true,
      onClick: getfileInteractionTracker('download'),
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "FileManagerPreview.download"
      })
    }, "download"), /*#__PURE__*/_jsx(UICopyButton, {
      "data-test-id": "file-preview-share-button",
      value: window.location.href,
      use: "secondary",
      onClick: getfileInteractionTracker('share'),
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "FileManagerPreview.share"
      })
    }, "share"), this.renderShare24HourLinkButton()];
  }
  render() {
    return /*#__PURE__*/_jsx(UIListingPage, {
      headerComponent: /*#__PURE__*/_jsx(UIHeader, {
        "data-test-id": "file-preview-header",
        title: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "FileManagerPreview.title"
        }),
        flush: true,
        children: this.renderButtons()
      }),
      children: this.renderBody()
    });
  }
}
Page.propTypes = {
  fileId: PropTypes.string.isRequired
};
export default Page;